import React,{useEffect,useMemo,useState} from 'react'
import axios from 'axios'
import {useNavigate} from 'react-router-dom'
import moment from 'moment'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import './lists.css'

/* ------------------------------------------------------------------
   Icons — stroke: currentColor so they inherit the token colour.
   ------------------------------------------------------------------ */
const IconOpen=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
  </svg>
)

const IconX=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
)

const IconSearch=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7"/>
    <path d="m20 20-3.5-3.5"/>
  </svg>
)

/* ------------------------------------------------------------------
   THEME TOKENS
   react-data-table-component styles through a JS object, so the table
   cannot read CSS variables on its own. We read them off :root and
   re-read whenever the theme attribute or the OS preference changes,
   which keeps the table in step with the rest of the product.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{
    out[name]=String(styles.getPropertyValue(name)||'').trim()
  })
  return out
}

function useThemeTokens(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      /* read after the new custom properties have landed */
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return tokens
}

const plural=(n,one,many)=>(n===1 ? one : many)

/* "2024-03-01T00:00:00.000Z" -> "1 Mar 2024"; anything odd falls back to the raw day. */
const dayOf=(value)=>{
  const raw=String(value||'')
  const day=raw.split('T')[0]
  const m=moment(raw)
  return m.isValid() ? m.format('D MMM YYYY') : day
}

export default function Lists() {
    const navigate=useNavigate ()
    const [lists,setLists]=useState([])
    /* true from the first paint: loadLists() fires on mount, so an empty-state
       flash before the request starts would be a lie */
    const [isLoading,setIsLoading]=useState(true)

    /* inline feedback — this is what replaced the alert() call */
    const [note,setNote]=useState(null)
    const [failed,setFailed]=useState(false)
    const [q,setQ]=useState('')

    const tokens=useThemeTokens()

    const openList=(uuid)=>navigate('/list/'+uuid)

    const columns = [
      {
          name: '#',
          selector: (row,index) =>  index+1,
          sortable: false,
          width: '58px',
          center: true
      },
      {
          name: 'Name',
          selector: (row,index) =>  row.listName,
          cell: row => (
            <button
              type="button"
              className="ls-name"
              title="Open this list"
              onClick={()=>openList(row.UUID)}
            >
              {row.listName}
            </button>
          ),
          sortable: true,
          minWidth: '220px',
          grow: 3
      },
      {
        name: 'Date Created',
        selector: (row,index) =>  String(row.DateCreated||'').split('T')[0],
        cell: row => <span className="ls-date">{dayOf(row.DateCreated)}</span>,
        sortable: true,
        minWidth: '150px'
      },
      {
        name: '',
        cell: row => (
          <button
            type="button"
            className="ls-rowbtn"
            title="Open this list"
            aria-label={'Open '+(row.listName||'list')}
            onClick={()=>openList(row.UUID)}
          >
            <IconOpen/>
          </button>
        ),
        width: '64px',
        center: true
      }
    ]
    useEffect(()=>{
      loadLists()
    },[])

    const loadLists=()=>{
      setIsLoading(true)
      setFailed(false)
      axios.get(process.env.REACT_APP_API_SERVER+'manager/listscriteria/lists/'+sessionStorage.getItem('userToken'),{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
              }
          })
        .then(res=>{
          if (res.data.err==0){
            //console.log(res.data.results)
            setLists(res.data.results)
            setNote(null)
          }else{
            setNote({tone:'crit',text:'Your saved lists could not be loaded. Please try again.'})
            setFailed(true)
          }

          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            setNote({tone:'crit',text:'Your saved lists could not be loaded. Check your connection and try again.'})
            console.log(err)
        })
    }

    /* Every colour below comes from theme/tokens.css via getComputedStyle,
       so the table repaints with the rest of the product on a theme change. */
    const tableStyles=useMemo(()=>{
      const t=tokens
      return {
        table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
        tableWrapper:{ style:{ display:'block' } },
        head:{ style:{ color:t['--ink-3'] } },
        headRow:{
          style:{
            backgroundColor:t['--panel-raise'],
            color:t['--ink-3'],
            minHeight:'42px',
            borderBottomWidth:'1px',
            borderBottomColor:t['--line'],
            borderBottomStyle:'solid'
          }
        },
        headCells:{
          style:{
            paddingLeft:'12px',
            paddingRight:'12px',
            fontFamily:t['--mono'],
            fontSize:'10px',
            fontWeight:600,
            letterSpacing:'0.08em',
            textTransform:'uppercase',
            color:t['--ink-3']
          }
        },
        rows:{
          style:{
            backgroundColor:t['--panel'],
            color:t['--ink'],
            fontFamily:t['--sans'],
            fontSize:t['--step-0']||'0.875rem',
            minHeight:'46px',
            '&:not(:last-of-type)':{
              borderBottomWidth:'1px',
              borderBottomStyle:'solid',
              borderBottomColor:t['--line-soft']
            }
          },
          highlightOnHoverStyle:{
            backgroundColor:t['--accent-wash'],
            color:t['--ink'],
            borderBottomColor:t['--line-soft'],
            outlineWidth:'0px',
            outlineColor:t['--panel'],
            transitionDuration:'0.15s',
            transitionProperty:'background-color'
          }
        },
        cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
        pagination:{
          style:{
            backgroundColor:t['--panel'],
            color:t['--ink-3'],
            fontSize:'12px',
            minHeight:'50px',
            borderTopWidth:'1px',
            borderTopStyle:'solid',
            borderTopColor:t['--line']
          },
          pageButtonsStyle:{
            color:t['--ink-2'],
            fill:t['--ink-2'],
            backgroundColor:'transparent',
            '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
            '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
            '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
          }
        },
        noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'], padding:'26px' } },
        progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
      }
    },[tokens])

    const total=Array.isArray(lists) ? lists.length : 0

    const shown=useMemo(()=>{
      const rows=Array.isArray(lists) ? lists : []
      const needle=q.trim().toLowerCase()
      if (!needle) return rows
      return rows.filter(item=>String(item.listName||'').toLowerCase().includes(needle))
    },[lists,q])

  return (
    <div className="ls-page">

      <div className="ls-head">
        <div className="ls-headline">
          <span className="mono ls-eyebrow">Saved searches</span>
          <h1 className="ls-title">Players' Lists</h1>
          <p className="ls-head-sub">
            Every set of scouting criteria you have saved. Open one to adjust its filters,
            rerun it against the current player pool, or export the players it returns.
          </p>
        </div>
        <div className="ls-head-actions">
          <Btn onClick={()=>navigate('/playerslist')}>Run a list</Btn>
          <Btn variant="accent" onClick={()=>navigate('/list')}>+ Create New List</Btn>
        </div>
      </div>

      {note
        ? <div className="ls-note" data-tone={note.tone} role="alert">
            <span>{note.text}</span>
            <button type="button" className="ls-note-x" aria-label="Dismiss" onClick={()=>setNote(null)}>
              <IconX/>
            </button>
          </div>
        : null}

      <Panel
        title="Saved lists"
        subtitle={
          isLoading
            ? 'Loading…'
            : total===0
              ? 'Nothing saved yet'
              : total+' '+plural(total,'list','lists')+' saved'
        }
        actions={
          total>0
            ? <span className="ls-search">
                <IconSearch/>
                <input
                  className="ls-input"
                  type="search"
                  value={q}
                  placeholder="Filter by name"
                  aria-label="Filter lists by name"
                  onChange={(e)=>setQ(e.target.value)}
                />
              </span>
            : null
        }
      >
        {isLoading
          ? <div className="ls-sk" aria-hidden="true">
              <span className="skeleton"></span>
              <span className="skeleton"></span>
              <span className="skeleton"></span>
              <span className="skeleton"></span>
            </div>
          : failed
            ? <div className="ls-empty">
                <div className="ls-empty-t">Your lists could not be loaded</div>
                <div className="ls-empty-s">
                  The server did not answer. Nothing has been lost — try loading them again.
                </div>
                <div className="ls-empty-a">
                  <Btn variant="accent" onClick={loadLists}>Try again</Btn>
                </div>
              </div>
            : total===0
              ? <div className="ls-empty">
                  <div className="ls-empty-t">No saved lists yet</div>
                  <div className="ls-empty-s">
                    A list stores a set of scouting criteria — age, height, country, position —
                    so you can rerun the same search whenever the player pool changes.
                  </div>
                  <div className="ls-empty-a">
                    <Btn variant="accent" onClick={()=>navigate('/list')}>+ Create New List</Btn>
                  </div>
                </div>
              : shown.length===0
                ? <div className="ls-empty">
                    <div className="ls-empty-t">No list matches “{q.trim()}”</div>
                    <div className="ls-empty-s">
                      {total} {plural(total,'list is','lists are')} saved. Clear the filter to see
                      {total===1 ? ' it' : ' them all'}.
                    </div>
                    <div className="ls-empty-a">
                      <Btn onClick={()=>setQ('')}>Clear filter</Btn>
                    </div>
                  </div>
                : <>
                    <div className="ls-count">
                      <span className="ls-count-n num">{shown.length}</span>
                      <span className="ls-count-l">
                        {plural(shown.length,'saved list','saved lists')}
                        {q.trim() ? ' matching your filter.' : ' ready to run.'}
                      </span>
                    </div>

                    <div className="ls-table">
                      <DataTable
                        columns={columns}
                        data={shown}
                        customStyles={tableStyles}
                        highlightOnHover
                        persistTableHead
                        pagination
                        paginationPerPage={25}
                        paginationRowsPerPageOptions={[25,50,100,200]}
                        noDataComponent={<div className="ls-empty-s">No lists to show.</div>}
                      />
                    </div>
                  </>}
      </Panel>
    </div>
  )
}
